import type { LinkFragment } from '@seek/cmsu-cms-connect';

/**
 * List of URLs that we don't want to forward the UTM parameters to.
 * These URL is going to be 404ed if we forward the UTM parameters to them.
 */
const unsupportedUTMForwardingWhiteList = [
  'https://forms.office.com/Pages/ResponsePage.aspx',
];

export const toUrl = (
  to: LinkFragment['to'],
  language: string = 'en',
  utmParameters = '',
): string => {
  const isNotEn = Boolean(language) && language !== 'en';
  switch (to?.__typename) {
    case 'ExternalUrl':
      // Check if the external URL is in the whitelist
      const shouldAppendUTM =
        to.externalUrl &&
        !unsupportedUTMForwardingWhiteList.some((whitelistedUrl) =>
          to.externalUrl?.startsWith(whitelistedUrl),
        );

      // Only append UTM parameters if:
      // 1. URL is not in whitelist
      // 2. URL is not a hash link
      if (
        utmParameters &&
        shouldAppendUTM &&
        to.externalUrl?.slice(0, 1) !== '#'
      ) {
        // we had a bug where we were appending the UTM parameters to the URL even if it already had them
        // so we need to check if the URL already has a ? or & and use the correct separator
        const separator = to.externalUrl?.includes('?') ? '&' : '?';
        return to.externalUrl?.concat(`${separator}${utmParameters}`) || '#';
      }
      return to.externalUrl || '#';
    case 'Asset':
      return to.url;
    case 'Article':
      const articleSection = to.category?.relatedCategorySection?.sectionName;
      const articleParentSection =
        to.category?.relatedCategorySection?.parentSection?.sectionName;
      const articleCompleteSection = [articleParentSection, articleSection]
        .filter(Boolean)
        .join('/');
      // assuming hirer articles always have a section so the article without section is from candidate site
      const articlePath = articleSection
        ? `/${articleCompleteSection}/article/${to.slug}`
        : `/career-advice/article/${to.slug}`;
      return isNotEn ? `/${language}${articlePath}` : articlePath;
    case 'Page':
      const pageSection = to.relatedPageSection?.sectionName;
      const pageParentSection =
        to.relatedPageSection?.parentSection?.sectionName;
      const pageCompleteSection = [pageParentSection, pageSection]
        .filter(Boolean)
        .join('/');
      let pagePath = null;
      // Remove the prefix from the slug for the Award sites
      const filteredSlug = to.slug?.replace(/(starawards-)|(sara-)/g, '');
      if (pageSection) {
        pagePath =
          pageSection === to.slug
            ? `/${pageCompleteSection}`
            : `/${pageCompleteSection}/${filteredSlug}`;
      } else {
        pagePath = `/page/${filteredSlug}`;
      }
      return isNotEn ? `/${language}${pagePath}` : pagePath;
    case 'Category':
      const categorySection = to.relatedCategorySection?.sectionName;
      const categoryParentSection =
        to.relatedCategorySection?.parentSection?.sectionName;
      const categoryCompleteSection = [categoryParentSection, categorySection]
        .filter(Boolean)
        .join('/');
      const categoryPath = `/${categoryCompleteSection}/category/${to.slug}`;
      return isNotEn ? `/${language}${categoryPath}` : categoryPath;
    default:
      return '#!';
  }
};
